import { useCallback, useSyncExternalStore } from 'react'

/** Light first; dark is a CSS-variable swap (src/styles/tokens.css). */
export type ThemePref = 'light' | 'dark' | 'system'
const KEY = 'ucsc.theme'
const listeners = new Set<() => void>()

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'dark' || v === 'system' ? v : 'light'
  } catch {
    return 'light'
  }
}

function resolve(pref: ThemePref): 'light' | 'dark' {
  if (pref !== 'system') return pref
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyStoredTheme(): void {
  const theme = resolve(readPref())
  document.documentElement.dataset.theme = theme
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'dark' ? '#181615' : '#221e1f')
}

export function useTheme(): [ThemePref, (p: ThemePref) => void] {
  const pref = useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    readPref,
    () => 'light' as ThemePref,
  )
  const set = useCallback((p: ThemePref) => {
    try {
      localStorage.setItem(KEY, p)
    } catch {
      /* ignore */
    }
    applyStoredTheme()
    listeners.forEach((l) => l())
  }, [])
  return [pref, set]
}
